/**
 * Generic codec for the numeric AutoCAD HEADER system-variable family.
 *
 * Several HEADER var groups share the exact same on-disk encoding: a typed
 * object whose every field is an optional `number`, persisted as alternating
 * `9 $NAME` + `<code> <value>` pairs where each var carries a single DXF group
 * code (40 = real, 70 = int16). `headerLinetypeVars.ts`
 * ($LTSCALE/$CELTSCALE/$PSLTSCALE/$MSLTSCALE) and `headerDocumentVars.ts`
 * ($INSUNITS/$PDMODE/$PDSIZE) are two instances of this pattern; only the var
 * table (name → field → group code → int-vs-real) differs.
 *
 * This module factors the read / write / patch primitives into one truth,
 * parameterized by a {@link HeaderVarSpec} descriptor list. Each domain module
 * keeps its own public typed object + read / write / patch API and simply
 * delegates the mechanics here.
 *
 * NOTE: deliberately scoped to the *numeric, single-code-per-var* family. The
 * annotative family (`headerAnnotativeVars.ts`, mixed string/bool/float/int,
 * codes 1/40/70/290) and the DYN family (`headerDynVars.ts`, keyed without the
 * `$` prefix, `Partial<Record>` shape) have different value typing / key
 * conventions and are intentionally NOT folded in here.
 *
 * AutoCAD reference: drawing system-variable reference 2018+.
 */

import type { DxfGroup } from "./types";

/**
 * Descriptor for one numeric HEADER variable.
 *
 * @typeParam V - the domain's typed vars object (e.g. `HeaderLinetypeVars`).
 */
export type HeaderVarSpec<V> = {
  /** Full `$NAME` token as written on the group-9 line. */
  name: string;
  /** Field key on the typed vars object that carries the decoded value. */
  field: keyof V;
  /** DXF group code carrying the value (40 = real, 70 = int16). */
  code: number;
  /** True → encode/decode as a rounded integer; false → float. */
  int: boolean;
  /**
   * The AutoCAD default for this var. When defined, an ABSENT var (one not
   * already present in the HEADER groups, or a fresh minimal-HEADER) is only
   * emitted when its value differs from this default — so a default value is
   * never *invented* into a header that did not carry the var. A var that
   * ALREADY EXISTS in the header is always overwritten in-place regardless of
   * this default, so an edit back to the default value still persists.
   *
   * Undefined → the var is always emitted when present on the typed object
   * (the original linetype-scale behaviour, whose default is not 0).
   */
  defaultValue?: number;
};

/** Format a numeric var value for serialization (rounded if integer-typed). */
function formatVarValue(value: number, int: boolean): string {
  return int ? String(Math.round(value)) : String(value);
}

/**
 * Scan a HEADER section's flat group list and extract the numeric sysvars
 * described by `specs`. Groups are expected in the AutoCAD pattern:
 *
 *   { code: 9, value: "$NAME" }
 *   { code: <typed>, value: "..." }
 *
 * Unknown variables (not in `specs`) are ignored. A name match with an
 * unexpected value code is ignored too — keeps the reader robust against
 * AutoCAD format updates. Non-finite values are skipped.
 */
export function readNumericVarsFromHeader<V extends object>(
  headerGroups: ReadonlyArray<DxfGroup>,
  specs: ReadonlyArray<HeaderVarSpec<V>>,
): V {
  const result: V = {} as V;
  const byName = new Map(specs.map((s) => [s.name, s]));

  for (let i = 0; i < headerGroups.length - 1; i++) {
    const nameGroup = headerGroups[i];
    const valueGroup = headerGroups[i + 1];
    if (!nameGroup || !valueGroup) continue;
    if (nameGroup.code !== 9) continue;
    const spec = byName.get(nameGroup.value.trim());
    if (!spec) continue;
    // Name matches but value carries an unexpected code → ignore without
    // consuming the value group (mirrors the original per-var readers).
    if (valueGroup.code !== spec.code) continue;

    const n = spec.int
      ? Number.parseInt(valueGroup.value, 10)
      : Number.parseFloat(valueGroup.value);
    if (Number.isFinite(n)) {
      (result[spec.field] as number) = n;
    }
    i++; // consume the matched value group
  }

  return result;
}

/**
 * Produce the DxfGroup pairs for the numeric HEADER variables described by
 * `specs`. Symmetric with {@link readNumericVarsFromHeader} — round-trip safe.
 * Only fields present (not `undefined`) on `vars` are emitted, in spec order.
 *
 * This writer feeds the minimal-HEADER fallback (no existing HEADER section),
 * so it is treated as an APPEND into an empty header: a spec with a defined
 * `defaultValue` is skipped when its value equals that default, so an absent
 * source var is never invented into a freshly synthesised HEADER.
 */
export function writeNumericVarsToHeaderLines<V extends object>(
  vars: V,
  specs: ReadonlyArray<HeaderVarSpec<V>>,
): DxfGroup[] {
  const out: DxfGroup[] = [];
  for (const { name, field, code, int, defaultValue } of specs) {
    const value = vars[field] as number | undefined;
    if (value === undefined) continue;
    if (defaultValue !== undefined && value === defaultValue) continue;
    out.push({ code: 9, value: name });
    out.push({ code, value: formatVarValue(value, int) });
  }
  return out;
}

/**
 * Splice numeric HEADER variable entries into an existing HEADER section group
 * array.
 *
 * 1. Walk the existing groups. When a `{ code: 9, value: "$NAME" }` entry is
 *    found and `vars` carries the corresponding field, replace the following
 *    value group with the new value (matching the spec's group code). This is
 *    an UNCONDITIONAL in-place overwrite — even a value edited back to the
 *    spec's `defaultValue` is written, so the export reflects the current
 *    document value rather than the stale raw bytes (Codex #248 F1).
 * 2. Append any variables present in `vars` but ABSENT from the existing groups,
 *    in spec order — but only when the value differs from the spec's
 *    `defaultValue` (when one is defined), so an absent-in-source default is
 *    never invented into the header. Specs without a `defaultValue` always
 *    append (linetype-scale behaviour, default ≠ 0).
 *
 * Every other HEADER variable is preserved verbatim and the section is not
 * reordered, keeping the file as close to the original AutoCAD output as
 * possible.
 */
export function patchNumericVarsIntoHeaderGroups<V extends object>(
  existingGroups: ReadonlyArray<DxfGroup>,
  vars: V,
  specs: ReadonlyArray<HeaderVarSpec<V>>,
): DxfGroup[] {
  if (Object.keys(vars).length === 0) return existingGroups.slice();

  const byName = new Map(specs.map((s) => [s.name, s]));
  const handled = new Set<keyof V>();
  const patched: DxfGroup[] = [];

  for (let i = 0; i < existingGroups.length; i++) {
    const group = existingGroups[i]!;
    if (group.code === 9) {
      const spec = byName.get(group.value.trim());
      if (spec && (vars[spec.field] as number | undefined) !== undefined) {
        patched.push(group); // keep the $NAME group as-is
        patched.push({
          code: spec.code,
          value: formatVarValue(vars[spec.field] as number, spec.int),
        });
        handled.add(spec.field);
        i++; // skip the original value group (replaced)
        continue;
      }
    }
    patched.push(group);
  }

  // Append any vars not found in the original section — but only when the
  // value is non-default (so an absent-in-source default is not invented).
  for (const { name, field, code, int, defaultValue } of specs) {
    if (handled.has(field)) continue;
    const value = vars[field] as number | undefined;
    if (value === undefined) continue;
    if (defaultValue !== undefined && value === defaultValue) continue;
    patched.push({ code: 9, value: name });
    patched.push({ code, value: formatVarValue(value, int) });
  }

  return patched;
}
